/**
 * Blob → rows. Every pure read of a `DeckBlob` lives here.
 *
 * Four adapters, one per directory kind, plus the `entity` resolver `DeckContent` requires and the id
 * counter every write needs. No React, no fetch, no DOM — which is the point: the mapping from the
 * canvas's storage format to the 46px row is the part that is easy to get subtly wrong, so it is the
 * part that gets unit tests.
 *
 * THREE CONTRACT RULES from `v1_5/Directory/theme.ts` that the component does NOT enforce and that this
 * file is therefore solely responsible for:
 *
 *  1. `meta` is written VERBATIM. The row never assembles it. The separator is U+00B7 with a space
 *     either side (`SEP` below), and images use U+00D7 for their dimensions because a dimension is not
 *     a list.
 *  2. While a lifecycle state is active the STATUS WORD takes the meta line's first slot and the
 *     timestamp is dropped — `Writing · Artifact 05`, `Queued`, `Updating`. A relative time on
 *     something that does not exist yet is a lie.
 *  4. Every meta line opens on a CAPITAL (Beka, 2026-09-05). These read as labels in a column, not as
 *     sentence fragments, and `Document · 1 page` beside `Writing document` — which was already
 *     capitalised — was the tell that the lowercase ones were the odd ones out.
 *  3. Websites carry `site: 'live' | 'down'` — the dot — derived from `BirtvyWeb.status`.
 */
import type {
  DirectoryItem,
  DirectoryKind,
} from '~/design-system/components/v1_5/Directory';
import { DOC_H, type BirtvyDoc } from '~/design-system/components/DeckGen/birtvy/birtvyDoc';
import type { BirtvyImage } from '~/design-system/components/DeckGen/birtvy/birtvyImage';
import type { BirtvySlide } from '~/design-system/components/DeckGen/birtvy/birtvySlide';
import { hostnameOf, type BirtvyWeb } from '~/design-system/components/DeckGen/birtvy/web/birtvyWeb';
import { blankSlideHtml } from '~/design-system/components/DeckGen/birtvyOpen/birtvyOpenDoc';
import { formatVersionTimestamp } from '~/utils/date-utils';
import { EMPTY_DECK_CONTENT, type DeckBlob, type DeckContent, type DeckEntity } from '../deckContent';
import { bindingFromBlob, kitFromBlob } from '../generate/artifactTheme';
import { itemKey, parseItemKey } from '../ids';

/** U+00B7 with a space either side. The separator every meta line uses. */
const SEP = ' · ';

/** `4` → `'04'`. The design numbers artifacts with a leading zero. */
const ordinal = (n: number): string => String(n).padStart(2, '0');

/** Fallback title. Deliberately the same word for every kind — an untitled thing is untitled. */
const UNTITLED = 'Untitled';

/**
 * A CSS `image` value for a source URL. `background-image` is parsed as CSS, so the two characters that
 * can break out of the quoted form are escaped rather than trusted; a signed S3 screenshot URL or a
 * multi-megabyte data: URL both go through unchanged.
 */
function cssUrl(src: string): string {
  return `url("${src.replace(/["\\]/g, (c) => `\\${c}`).replace(/\n/g, '')}")`;
}

// ── Artifacts ────────────────────────────────────────────────────────────────────────────────────────

/**
 * An artifact is a slide with `docHtml` — an open-slide-class complete HTML document. Every other slide
 * in the blob is a flow slide (baked elements, or nothing yet) and belongs to the canvas, not here.
 *
 * The test is PRESENCE, not truthiness: a doc generation streams into `docHtml` and passes through `''`
 * on its way, and a row that vanishes for the first second of its own generation is worse than a row
 * with no title yet.
 */
export function isArtifactSlide(slide: BirtvySlide): boolean {
  return slide.docHtml !== undefined;
}

/** `blankSlideHtml()` is a pure function of nothing; called once so the check below is a string
 *  comparison rather than a re-wrap per slide per render. */
const BLANK_DOC = blankSlideHtml();

/**
 * HAS THIS ARTIFACT ACTUALLY BEEN WRITTEN? — and the answer is the document, not the status field.
 *
 * `status` is a hint maintained by whoever did the writing, and not every writer maintains it. An
 * artifact minted by `artifactBatch` starts `status: 'empty'` with `blankSlideHtml()` in it, and the
 * in-app generation clears that to `'done'` when it finishes. The AGENT path does not: it writes the
 * document through the hosted MCP server, which sets `docHtml` and leaves `status` where it found it.
 *
 * So a fully generated artifact sat at `'empty'` forever — the preview rendered it, the card called it
 * "Queued", and `useDeckBakes` skipped it by that same status, so it could never get a thumbnail
 * either. Beka caught it with the artifact open on screen and the header still saying Queued
 * (2026-09-10).
 *
 * Fixing the writer is the other half and it lives on the server. This is the half that belongs here:
 * a slide holding a real document is not queued, whatever its status says. The document is the fact
 * and the status is a claim about it, so where they disagree the document wins.
 */
export function hasWrittenDoc(slide: BirtvySlide): boolean {
  const html = slide.docHtml;
  if (!html) return false;
  // The exact string `artifactBatch` mints. Anything else is something a writer put there.
  return html !== BLANK_DOC;
}

/**
 * When this artifact's source was last written, from the newest entry of its activity log.
 *
 * `BirtvySlide` has NO timestamp field of its own — `docLog` is the only clock in the record, and it
 * only exists for artifacts written through `writeDocSource` (anything older, or seeded from a real
 * deck, has none). When there is nothing to read the time half of the meta line is DROPPED rather than
 * guessed; `artifact 04` alone is honest, `artifact 04 · 2h ago` on an unknown date is not.
 */
function lastWriteAt(slide: BirtvySlide): number | undefined {
  const log = slide.docLog;
  const last = log && log.length > 0 ? log[log.length - 1] : undefined;
  return typeof last?.at === 'number' ? last.at : undefined;
}

export function artifactItems(slides: BirtvySlide[] | undefined): DirectoryItem[] {
  return (slides ?? []).filter(isArtifactSlide).map((slide, index) => {
    const id = itemKey('artifacts', slide.id);
    const title = slide.docMeta?.title?.trim() || UNTITLED;
    const label = `Artifact ${ordinal(index + 1)}`;
    /**
     * QUEUED — but only if it is genuinely still empty.
     *
     * `slide.status` alone was the test, and it is not trustworthy: the agent path writes the document
     * without clearing it, so a finished artifact kept calling itself queued. `hasWrittenDoc` asks the
     * document instead. See its note.
     *
     * QUEUED, and it has to SAY so in its status and not only in its meta line.
     *
     * This branch omitted `status` while the `generating` branch two lines down sets one, and the
     * website adapter in this same file sets `status: 'queued'` for the identical state. Everything
     * downstream reads `item.status ?? 'ready'`, so a queued artifact was a FINISHED one to every
     * component: renameable, carrying a `⋯` menu over a document that does not exist yet, and in row
     * layout drawing a ready chip's hairline with no queued glyph.
     *
     * The card showed it plainest — an empty grey plate whose own words said "Queued" while every
     * affordance on it said otherwise (Beka, 2026-09-10, with a screenshot of exactly this row).
     */
    if (slide.status === 'empty' && !hasWrittenDoc(slide))
      return { id, title, meta: `Queued${SEP}${label}`, status: 'queued' as const };

    if (slide.status === 'generating') {
      // Rule 2: the status word takes the timestamp's slot. `progress` is deliberately ABSENT — nothing
      // in the blob records how far a doc generation has got (the stream's byte count lives in React
      // state on the canvas and is never persisted), and the row reads `progress ?? 0`, so an omitted
      // value draws an empty bar instead of a fabricated percentage.
      return { id, title, meta: `Writing${SEP}${label}`, status: 'generating' as const };
    }

    const at = lastWriteAt(slide);
    const ago = at === undefined ? '' : formatVersionTimestamp(at);
    return {
      id,
      title,
      meta: ago ? `${label}${SEP}${ago}` : label,
      // THUMBNAIL DELIBERATELY OMITTED. `docBaked` is a static HTML STRING, not an image: the canvas
      // paints it by mounting it and rasterizing the DOM (BirtvySlideCard → BirtvyThumb), which is a
      // render pass, not a value a CSS `background-image` can take. There is no cheap conversion and a
      // screenshot pipeline is out of scope, so the row falls back to the artifact mark — correct, and
      // visibly honest. (`docBakedBackground` in BirtvySlideCard.tsx parses the document's own ground
      // colour out of the bake and is module-private; exporting it would give a colour chip, not a
      // render, if the team decides a chip beats the mark.)
    };
  });
}

/**
 * Rows for artifacts the agent is creating that the deck does not hold yet (`pendingArtifacts.ts`),
 * numbered on from the deck's own. They wear the generating state and carry `pending`, so the panel
 * draws them and wires nothing to them.
 */
export function pendingArtifactItems(keys: readonly string[], existing: number): DirectoryItem[] {
  return keys.map((key, index) => ({
    id: key,
    title: UNTITLED,
    meta: `Writing${SEP}Artifact ${ordinal(existing + index + 1)}`,
    status: 'generating' as const,
    pending: true,
  }));
}

// ── Documents ────────────────────────────────────────────────────────────────────────────────────────

/**
 * How many pages the card spans on the canvas — its height over one page (`DOC_H`, 1080).
 *
 * This is the canvas's own notion of a page and the only page count that exists: a `BirtvyDoc` is
 * markdown plus a box, with no pagination and no section index. The design's `document · 16 sections`
 * would need a markdown parse the row does not justify.
 */
export function docPages(doc: BirtvyDoc): number {
  const height = typeof doc.height === 'number' && doc.height > 0 ? doc.height : DOC_H;
  return Math.max(1, Math.ceil(height / DOC_H));
}

export function documentItems(docs: BirtvyDoc[] | undefined): DirectoryItem[] {
  return (docs ?? []).map((doc) => {
    const pages = docPages(doc);
    return {
      id: itemKey('documents', doc.id),
      // `title` is already derived from the markdown's leading heading (deriveDocTitle) at write time.
      title: doc.title?.trim() || UNTITLED,
      meta: doc.streaming ? 'Writing document' : `Document${SEP}${pages} page${pages === 1 ? '' : 's'}`,
      ...(doc.streaming ? { status: 'generating' as const } : {}),
    };
  });
}

// ── Images ───────────────────────────────────────────────────────────────────────────────────────────

/**
 * The filename off an image source, or undefined when there isn't one: a `data:` / `blob:` source is
 * pixels with no name, and inventing `image-3.png` for it would be worse than falling back.
 */
export function imageFilename(src: string): string | undefined {
  if (!src || src.startsWith('data:') || src.startsWith('blob:')) return undefined;
  try {
    // A base lets the relative `/conversation-images-api/...` URLs the pool serves parse too.
    const last = new URL(src, 'http://deck.local').pathname.split('/').filter(Boolean).pop();
    if (!last) return undefined;
    try {
      return decodeURIComponent(last);
    } catch {
      return last;
    }
  } catch {
    return undefined;
  }
}

export function imageItems(images: BirtvyImage[] | undefined): DirectoryItem[] {
  return (images ?? []).map((image) => ({
    id: itemKey('images', image.id),
    title: image.label?.trim() || imageFilename(image.src) || UNTITLED,
    /**
     * THE BOX, NOT THE FILE. `BirtvyImage` stores only its placement rect, and `imageInsertSize` clamps
     * the longest side to 640 world px on insert — so for anything bigger than that these numbers are
     * the card's size, not the picture's pixels. The natural size is knowable only by loading the image
     * (`measureImage`), which is async and a DOM call, and this adapter is neither. Printing the real
     * box beats printing a plausible `1920 × 1080` that no field in the blob supports.
     */
    meta: `${Math.round(image.width)} × ${Math.round(image.height)}`,
    thumbnail: image.src ? cssUrl(image.src) : undefined,
  }));
}

// ── Websites ─────────────────────────────────────────────────────────────────────────────────────────

/**
 * A real website card, as opposed to a brand-style tile.
 *
 * `blob.webs` is one collection holding two different things: website/social cards (`kind: 'web'`, or
 * absent on cards written before the field existed) and the swatch / fonts / logo tiles a page's style
 * extraction drops beside them. Tiles are a reference surface, not a site, and eight colour chips in
 * the Websites list would bury the one thing that is actually a website.
 */
export function isSiteCard(web: BirtvyWeb): boolean {
  return web.kind === 'web' || web.kind === undefined;
}

export function websiteItems(webs: BirtvyWeb[] | undefined): DirectoryItem[] {
  return (webs ?? []).filter(isSiteCard).map((web) => {
    const base = {
      id: itemKey('websites', web.id),
      // A user-given name outranks the page's own title, which outranks the bare hostname.
      // `UNTITLED` last, for the blank card the website intake opens on: its url is `''` until the
      // pill is submitted, and `hostnameOf('')` is `''` — which drew a row with no name at all.
      title: web.label?.trim() || web.title?.trim() || hostnameOf(web.url) || UNTITLED,
      thumbnail: web.screenshotUrl ? cssUrl(web.screenshotUrl) : undefined,
    };

    // Rule 2 again. A card mid-fetch and a card that has never been fetched are both in flight, so the
    // state word is the whole meta line and the dot is withheld — until the page answers there is no
    // honest answer to "is it live".
    if (web.status === 'loading') return { ...base, meta: 'Updating', status: 'updating' as const };
    if (web.status === 'empty') return { ...base, meta: 'Queued', status: 'queued' as const };

    // Rule 3. The design's `live · checked 5m ago` needs a checked-at timestamp. `checkedAt` records
    // exactly that since the re-fetch slice; cards saved before it existed still have no time, so the
    // second half is dropped rather than invented — same rule as an artifact with no log. `offline ·
    // 404` loses its status code for the same reason: nothing records it.
    const checked = web.checkedAt ? `${SEP}checked ${formatVersionTimestamp(web.checkedAt)}` : '';
    if (web.status === 'error') return { ...base, meta: `Offline${checked}`, site: 'down' as const };
    return { ...base, meta: `Live${checked}`, site: 'live' as const };
  });
}

// ── The whole blob ───────────────────────────────────────────────────────────────────────────────────

/**
 * Everything the four surfaces read, derived from one blob.
 *
 * `entity` resolves a row key back to the object behind it — the preview needs the artifact's HTML, the
 * document's markdown, the image's source, the site's URL, and `DirectoryItem` deliberately carries
 * none of that. Built over maps rather than a scan so a 200-artifact deck does not go quadratic when
 * every pane resolves its own selection on every render.
 */
export function toDeckContent(blob: DeckBlob | null | undefined): DeckContent {
  if (!blob) return EMPTY_DECK_CONTENT;

  const slides = (blob.slides ?? []).filter(isArtifactSlide);
  const docs = blob.docs ?? [];
  const images = blob.images ?? [];
  const webs = (blob.webs ?? []).filter(isSiteCard);

  const bySlide = new Map(slides.map((s) => [s.id, s]));
  const byDoc = new Map(docs.map((d) => [d.id, d]));
  const byImage = new Map(images.map((i) => [i.id, i]));
  const byWeb = new Map(webs.map((w) => [w.id, w]));

  const entity = (key: string): DeckEntity | undefined => {
    const parsed = parseItemKey(key);
    if (!parsed) return undefined;
    switch (parsed.kind) {
      case 'artifacts': {
        const slide = bySlide.get(parsed.id);
        return slide ? { kind: 'artifacts', slide } : undefined;
      }
      case 'documents': {
        const doc = byDoc.get(parsed.id);
        return doc ? { kind: 'documents', doc } : undefined;
      }
      case 'images': {
        const image = byImage.get(parsed.id);
        return image ? { kind: 'images', image } : undefined;
      }
      case 'websites': {
        const web = byWeb.get(parsed.id);
        return web ? { kind: 'websites', web } : undefined;
      }
      default:
        return undefined;
    }
  };

  const items: Record<DirectoryKind, DirectoryItem[]> = {
    artifacts: artifactItems(blob.slides),
    documents: documentItems(docs),
    websites: websiteItems(blob.webs),
    images: imageItems(images),
  };

  return {
    title: (typeof blob.title === 'string' ? blob.title.trim() : '') || EMPTY_DECK_CONTENT.title,
    items,
    entity,
    // Rides the blob's index signature — this file never writes it, and every `mutate` carries it
    // through untouched. Surfaced here so generation and the overlays read the deck rather than
    // reaching into the react-query entry behind this hook's back.
    docTheme: typeof blob.docTheme === 'string' ? blob.docTheme : '',
    brandKit: blob.kitOptOut === true ? null : kitFromBlob(blob),
    // Surfaced even under opt-out: the binding stays stored so re-picking the kit is one click
    // (`optOutOfKitIn`), and generation reads `kitOptOut` before it reads this.
    brandKitBinding: bindingFromBlob(blob),
    kitOptOut: blob.kitOptOut === true,
    planningDocumentIds: (blob.agentDocs ?? {}) as Record<string, number>,
  };
}

// ── Ids ──────────────────────────────────────────────────────────────────────────────────────────────

/**
 * The blob's collections that carry numbered items. Slides, docs and images are typed on `DeckBlob`;
 * notes, wires and groups ride the index signature — but they are numbered from the SAME counter, so
 * they have to be counted or a new slide collides with an existing wire.
 */
const NUMBERED_COLLECTIONS = ['slides', 'docs', 'notes', 'images', 'webs', 'wires', 'groups'] as const;

/**
 * The next free item id: `max(every numeric id in the whole blob) + 1`.
 *
 * The canvas mints ids from ONE counter shared by every kind (`idRef`), and re-derives it on hydrate as
 * the max over slides, docs, notes, images, webs, wires AND groups (useBirtvySerialization). Anything
 * narrower here — "max slide id + 1" — hands out an id a wire or a brand tile already owns, and the two
 * items then fight over one row on the item-sync path. The rule is copied deliberately, not approximated.
 *
 * Consequence worth knowing: deleting the highest-numbered item frees its number for reuse, exactly as
 * on the canvas.
 */
export function nextBlobId(blob: DeckBlob): number {
  let next = 1;
  for (const name of NUMBERED_COLLECTIONS) {
    const rows = blob[name];
    if (!Array.isArray(rows)) continue;
    for (const row of rows) {
      const id = (row as { id?: unknown } | null)?.id;
      if (typeof id === 'number' && Number.isFinite(id) && id + 1 > next) next = id + 1;
    }
  }
  return next;
}
